// The technical account drawer: entries, issue, and settlement.
//
// This is where the money actually lands — "the posting". The balance below
// is what the server returned, not a local sum: after a settlement the list
// reloads and the drawer re-renders from the record, so the figure on screen
// is always the figure in the database.

import React, { useState } from 'react';
import {
  C, Pill, Panel, Row, MiniTable, NextAction, money0, date, humanise,
} from './kit';
import {
  useAction, Notice, ActionButton, ActionRow, ConfirmAction, Input, FieldRow,
  EditPanel, StatusAction, RemoveAction,
} from './actions';
import { DocumentsTab, ActivityTab } from './tabs';

const today = () => new Date().toISOString().slice(0, 10);

export default function AccountDrawer({ row: r, tab, reload }) {
  const act = useAction(reload);

  const [entry, setEntry] = useState({ kind: 'premium', description: '', amount: '' });
  // Named for the fields the ACCOUNT endpoint takes — settledAt and
  // paymentReference. Recoveries use receivedAt/reference; they are different
  // endpoints and the names are not interchangeable.
  const [pay, setPay] = useState({ amount: '', settledAt: today(), paymentReference: '' });
  const [reversing, setReversing] = useState('');
  const [reverseReason, setReverseReason] = useState('');
  // Entries that already have a reversal against them, so the button is not
  // offered twice on the same line.
  const reversedIds = new Set(
    (r.entries || []).filter(e => e.kind === 'reversal' && e.sourceId).map(e => String(e.sourceId)),
  );
  // What can still be reversed: anything that is not itself a correction and
  // does not already have one against it.
  const reversible = (r.entries || []).filter(
    e => e.kind !== 'reversal' && !reversedIds.has(String(e._id)),
  );

  const issued = r.status !== 'draft';
  const outstanding = Number(r.outstanding) || 0;

  // ─── Line items ───
  if (tab === 'Line Items') {
    return (
      <>
        <Notice error={act.error} note={act.note} />
        <Panel title='Entries'>
          {/* An entry is never edited and never deleted. A mistake is
              corrected by posting its opposite, which is what the server has
              always done — it just had no button, so a wrong figure on an
              issued account could only be lived with.
              Rendered as two-line rows rather than a table. The drawer is
              388px wide and three money columns do not fit it: the amount —
              the one figure that must never be cut off — sat behind a
              horizontal scrollbar, with the kind running into it. Description
              on top, kind and amount beneath, amount right-aligned and never
              wrapped. The reversal control is a form below, matching
              "Add an entry" underneath it. */}
          {(r.entries || []).length === 0 ? (
            <div style={{ fontSize: 12.5, color: C.faint }}>No entries on this account yet.</div>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 2 }}>
              {(r.entries || []).map((e, i) => {
                const marked = e.kind === 'reversal' ? 'correction'
                  : reversedIds.has(String(e._id)) ? 'reversed' : null;
                return (
                  <li key={e._id || i} style={{
                    padding: '8px 0',
                    borderBottom: i < r.entries.length - 1 ? `1px solid ${C.hair}` : 'none',
                  }}>
                    <div style={{ fontSize: 12.5, color: C.body, lineHeight: 1.4 }}>
                      {e.description || '—'}
                    </div>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                      gap: 10, marginTop: 3,
                    }}>
                      <span style={{ fontSize: 11.5, color: C.muted }}>
                        {humanise(e.kind)}{marked ? ` · ${marked}` : ''}
                      </span>
                      <span style={{
                        fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap',
                        fontVariantNumeric: 'tabular-nums',
                        color: Number(e.amount) < 0 ? C.muted : C.ink,
                      }}>
                        {money0(e.amount, e.currency || r.currency)}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>

        {reversible.length > 0 && (
          <Panel title='Reverse an entry'>
            <div style={{ display: 'grid', gap: 9 }}>
              <Input
                label='Entry'
                value={reversing || ''}
                onChange={setReversing}
                options={[
                  { value: '', label: 'Select an entry…' },
                  ...reversible.map(e => ({
                    value: String(e._id),
                    label: `${e.description || e.kind} — ${money0(e.amount, e.currency || r.currency)}`,
                  })),
                ]}
              />
              <Input
                label='Why is this being reversed?'
                value={reverseReason}
                onChange={setReverseReason}
                placeholder='Premium booked against the wrong period'
              />
              <div style={{ fontSize: 11.5, color: C.faint, lineHeight: 1.5 }}>
                An entry is never edited or deleted. This posts an equal and
                opposite line, so the account still shows what happened and why.
              </div>
              <ActionRow>
                <ActionButton
                  busy={act.saving}
                  disabled={!reversing || !reverseReason.trim()}
                  onClick={async () => {
                    const ok = await act.run({
                      url: `/reinsurance/accounts/${r._id}/entries/${reversing}/reverse`,
                      body: { reason: reverseReason.trim() },
                      success: 'Reversing entry posted — the original stays on the account.',
                    });
                    if (ok) { setReversing(''); setReverseReason(''); }
                  }}
                >
                  Post reversal
                </ActionButton>
              </ActionRow>
            </div>
          </Panel>
        )}

        <Panel title='Add an entry'>
          <div style={{ display: 'grid', gap: 9 }}>
            <Input
              label='Kind'
              value={entry.kind}
              onChange={(v) => setEntry(p => ({ ...p, kind: v }))}
              options={[
                { value: 'premium', label: 'Premium' },
                { value: 'commission', label: 'Commission' },
                { value: 'claim', label: 'Claim' },
                { value: 'adjustment', label: 'Adjustment' },
                { value: 'tax', label: 'Tax' },
              ]}
            />
            <Input
              label='Description'
              value={entry.description}
              onChange={(v) => setEntry(p => ({ ...p, description: v }))}
              placeholder='Q1 premium, Swiss Re 35%'
            />
            <Input
              label={`Amount (${r.currency || ''})`}
              type='number'
              value={entry.amount}
              onChange={(v) => setEntry(p => ({ ...p, amount: v }))}
            />
            <div style={{ fontSize: 11.5, color: C.faint, lineHeight: 1.5 }}>
              Entries are signed from our point of view: positive is receivable,
              negative is payable. A commission owed back to the cedant is negative.
            </div>
            <ActionRow>
              <ActionButton
                busy={act.saving}
                disabled={!entry.description.trim() || entry.amount === ''}
                onClick={async () => {
                  const ok = await act.run({
                    url: `/reinsurance/accounts/${r._id}/entries`,
                    body: {
                      kind: entry.kind,
                      description: entry.description.trim(),
                      amount: Number(entry.amount),
                      currency: r.currency,
                    },
                    success: 'Entry added.',
                  });
                  if (ok) setEntry({ kind: 'premium', description: '', amount: '' });
                }}
              >
                Add entry
              </ActionButton>
            </ActionRow>
          </div>
        </Panel>
      </>
    );
  }

  // ─── Settlement ───
  if (tab === 'Settlement') {
    return (
      <>
        <Notice error={act.error} note={act.note} />

        <Panel title='Settlement history'>
          <MiniTable
            head={['Received', 'Amount', 'Reference']}
            rows={(r.settlements || []).map(s => [
              date(s.settledAt),
              money0(s.amount, s.currency || r.currency),
              s.paymentReference || '—',
            ])}
            empty='Nothing settled against this account yet.'
          />
          <div style={{
            display: 'flex', justifyContent: 'space-between', marginTop: 11,
            paddingTop: 10, borderTop: `1px solid ${C.hair}`, fontSize: 13, fontWeight: 700,
          }}>
            <span style={{ color: C.muted }}>Outstanding</span>
            <span style={{ color: outstanding === 0 ? C.green : C.ink, fontVariantNumeric: 'tabular-nums' }}>
              {money0(r.outstanding, r.currency)}
            </span>
          </div>
        </Panel>

        <Panel title='Record a settlement'>
          {!issued ? (
            // The server refuses this with a 409, and saying so up front is
            // better than letting someone fill a form that cannot be sent.
            <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.55 }}>
              This account is still a draft. Issue it first — a settlement against
              an unissued account is money received for a statement the
              counterparty has never seen.
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 9 }}>
              <FieldRow>
                <Input
                  label={`Amount (${r.currency || ''})`}
                  type='number'
                  value={pay.amount}
                  onChange={(v) => setPay(p => ({ ...p, amount: v }))}
                />
                <Input
                  label='Date received'
                  type='date'
                  value={pay.settledAt}
                  onChange={(v) => setPay(p => ({ ...p, settledAt: v }))}
                />
              </FieldRow>
              <Input
                label='Payment reference'
                value={pay.paymentReference}
                onChange={(v) => setPay(p => ({ ...p, paymentReference: v }))}
                placeholder='TRF-2026-09-001'
              />
              <ActionRow>
                <ActionButton
                  busy={act.saving}
                  disabled={pay.amount === ''}
                  onClick={async () => {
                    const ok = await act.run({
                      url: `/reinsurance/accounts/${r._id}/settlements`,
                      body: {
                        amount: Number(pay.amount),
                        settledAt: pay.settledAt,
                        paymentReference: pay.paymentReference.trim(),
                        currency: r.currency,
                      },
                      success: 'Settlement recorded — the balance has moved.',
                    });
                    if (ok) setPay({ amount: '', settledAt: today(), paymentReference: '' });
                  }}
                >
                  Record settlement
                </ActionButton>
                <ActionButton
                  tone='quiet'
                  onClick={() => setPay(p => ({ ...p, amount: String(Math.abs(outstanding)) }))}
                >
                  Settle in full
                </ActionButton>
              </ActionRow>
            </div>
          )}
        </Panel>
      </>
    );
  }

  // ─── Activity ───
  // An account carries a documentation checklist — bordereaux, statement,
  // working papers — and had nowhere to show it. The tab was simply absent,
  // so the three documents an account needs before it can be issued could
  // only be seen by reading the Overview.
  if (tab === 'Documents') {
    return <DocumentsTab record={r} checklistTitle='Statement documents' />;
  }

  if (tab === 'Activity') return <ActivityTab resource='accounts' id={r._id} />;


  // ─── Overview ───
  return (
    <>
      <Notice error={act.error} note={act.note} />

      <Panel title='Account information'>
        <Row label='Account no.' value={r.accountNumber} />
        <Row label='Treaty ref' value={r.treatyNumber || r.dealNumber} />
        <Row label='Counterparty' value={r.counterpartyName} />
        <Row label='Period' value={`${date(r.periodFrom)} – ${date(r.periodTo)}`} />
        <Row label='Due date' value={date(r.dueDate)} />
        <Row label='Currency' value={r.currency} />
        {/* `balance` is an OBJECT — { debit, credit, net, dueTo } — so the
            figure is balance.net. Passing the object itself to a money
            formatter is what put a dash in this row on every account while
            the list column beside it, which reads balance.net, showed the
            real number. Direction comes from dueTo, the same as the list. */}
        <Row
          label='Balance'
          value={r.balance?.net == null ? null : (
            <>
              {money0(Math.abs(r.balance.net), r.currency)}
              <span style={{ color: C.muted, fontWeight: 400 }}>
                {r.balance.dueTo === 'us' ? ' due to us'
                  : r.balance.dueTo === 'counterparty' ? ' due to market' : ' nil'}
              </span>
            </>
          )}
          strong
        />
        <Row label='Outstanding' value={money0(r.outstanding, r.currency)} />
        <Row
          label='Overdue'
          value={r.daysOverdue > 0
            ? <span style={{ color: C.red, fontWeight: 700 }}>{r.daysOverdue}d</span>
            : null}
        />
        <Row label='Status' value={<Pill status={r.status} size='sm' />} />
      </Panel>

      {/* ── The record itself ──
          Correcting, moving and withdrawing a record, in one place on the
          Overview tab. Before this a screen could create a record and then
          never touch it again. Removal is a void or a cancel, never a
          delete — see reinsuranceShared.js. */}
      <Panel title='Record'>
        <div style={{ display: 'grid', gap: 10 }}>
          <EditPanel
            record={r}
            endpoint='/reinsurance/accounts'
            onSaved={reload}
            fields={[
            { key: 'periodFrom', label: 'Period from', type: 'date' },
            { key: 'periodTo', label: 'Period to', type: 'date' },
            { key: 'dueDate', label: 'Due date', type: 'date' },
            { key: 'notes', label: 'Notes' },
            ]}
          />
          <StatusAction
            record={r}
            endpoint='/reinsurance/accounts'
            onSaved={reload}
            options={[{ value: 'draft', label: 'Draft' }, { value: 'issued', label: 'Issued' }, { value: 'agreed', label: 'Agreed' }, { value: 'settled', label: 'Settled' }, { value: 'disputed', label: 'Disputed' }]}
          />
          <RemoveAction record={r} endpoint='/reinsurance/accounts' noun='account' onRemoved={reload} />
        </div>
      </Panel>

      <Panel title='Actions'>
        <ActionRow>
          {!issued && (
            <ConfirmAction
              label='Issue account'
              question={`Issue ${r.accountNumber} to ${r.counterpartyName}? Entries are fixed once issued and settlement can begin.`}
              confirmLabel='Issue'
              busy={act.saving}
              onConfirm={() => act.run({
                url: `/reinsurance/accounts/${r._id}/issue`,
                success: 'Account issued. It can now be settled.',
              })}
            />
          )}
          {issued && outstanding !== 0 && (
            <ActionButton
              busy={act.saving}
              onClick={() => act.run({
                url: `/reinsurance/accounts/${r._id}/settlements`,
                body: {
                  amount: Math.abs(outstanding),
                  settledAt: today(),
                  paymentReference: `SETTLE-${r.accountNumber}`,
                  currency: r.currency,
                },
                success: 'Settled in full.',
              })}
            >
              Settle in full
            </ActionButton>
          )}
        </ActionRow>
      </Panel>

      <Panel title='Statement documents'>
        <MiniTable
          head={['Document', 'Status']}
          rows={(r.requiredDocuments || []).map(d => [
            d.label,
            d.uploaded ? 'Uploaded' : 'Not uploaded',
          ])}
          empty='No document checklist on this account.'
        />
      </Panel>

      {r.nextAction?.label && (
        <NextAction label={r.nextAction.label} detail={r.nextAction.detail} />
      )}
    </>
  );
}
